import { useDebouncedEffect } from '@timelyapp/react';
import classNames from 'classnames';
import ClearableButton from 'components/buttons/ClearableButton';
import type { DropdownOption, OnDropdownChange } from 'components/DropdownBuilder';
import DropdownBuilder from 'components/DropdownBuilder';
import Popover from 'components/popovers/Popover';
import Spinner from 'components/Spinner';
import { useOnOff } from 'hooks/useOnOff';
import React, { useCallback, useMemo, useState } from 'react';
import FontAwesome from 'react-fontawesome';
import type { Client } from 'types/client';
import { countableLabel } from 'utils/text-helper';
import FilterDropDownItem from './FilterDropdownItem';
import FilterGroups from './FilterGroups';
import styles from './ProjectFilterDropdown.module.scss';

interface ClientFilterDropdownProps {
  clients: Array<Client>;
  onChange?: (clientIds: Array<number>) => void;
  onClear?: () => void;
  onSelectClient?: (clientId: number) => void;
  onDeselectClient?: (clientId: number) => void;
  onSetFilterGroup?: (group: 'all' | 'active' | 'archive') => void;
  selected: Array<number>;
  unrestrictedWhenEmpty?: boolean;
  allowSelectNone?: boolean;
  emptyLabel?: string;
  disabled?: boolean;
  disableAll?: boolean;
  onSearch?: (filterString: string, filterGroup?: 'all' | 'active' | 'archive') => Promise<unknown>;
  clientFetchingStatus?: 'loading' | 'loaded';
  onSelectAll?: () => void;
  position?: 'bottom' | 'top';
  fixed?: boolean;
  isLoading?: boolean;
  isInitiallyOpen?: boolean;
  size?: 'small' | 'medium';
  testId?: string;
}

const ClientFilterDropdown = (props: ClientFilterDropdownProps) => {
  const {
    allowSelectNone = true,
    clients,
    disabled,
    disableAll,
    emptyLabel,
    fixed = false,
    isInitiallyOpen = false,
    isLoading,
    onChange,
    onClear,
    onDeselectClient,
    onSearch,
    onSelectClient,
    onSetFilterGroup,
    position = 'bottom',
    selected,
    unrestrictedWhenEmpty,
    size,
  } = props;

  const [isOpen, setIsOpen] = useState<boolean>(isInitiallyOpen);
  const [setOpen, setClosed] = useOnOff(setIsOpen);
  const [filterGroup, setFilterGroupInState] = useState<'all' | 'active' | 'archive'>('active');
  const selectedSet = useMemo(() => new Set(selected), [selected]);

  const setFilterGroup = (g: 'all' | 'active' | 'archive') => {
    setFilterGroupInState(g);
    onSetFilterGroup?.(g);
  };

  const filteredClients = useMemo(() => {
    const mappedClients = clients.map(client => ({
      ...client,
      label: client.name,
      value: client.id,
    }));
    if (filterGroup === 'all') {
      return mappedClients;
    }
    return mappedClients.filter(client => client.active === (filterGroup === 'active'));
  }, [filterGroup, clients]);

  const [filterString, onSetFilterString] = useState('');
  useDebouncedEffect(
    () => onSearch?.(filterString, filterGroup),
    [filterGroup, filterString, onSearch],
    300
  );

  const handleClear = useCallback(
    (e?: React.SyntheticEvent) => {
      e?.stopPropagation();
      onChange?.([]);
      onClear?.();
    },
    [onChange, onClear]
  );

  const numSelected = selected.length;
  const numClients = clients.length;
  const emptySelection = numSelected === 0;
  const isEmptyAndUnrestricted = unrestrictedWhenEmpty && emptySelection;
  const showClearButton = unrestrictedWhenEmpty && !emptySelection;
  const isSelected = !!numSelected && !isEmptyAndUnrestricted;

  const filterGroups: { value: 'all' | 'active' | 'archive'; name: string }[] = [
    { value: 'all', name: 'All clients' },
    { value: 'active', name: 'Active' },
    { value: 'archive', name: 'Archived' },
  ];

  let label = isEmptyAndUnrestricted
    ? 'Any client'
    : countableLabel('client', { selected: numSelected, total: numClients });
  if (emptySelection && !isEmptyAndUnrestricted) {
    label = emptyLabel!;
  }

  const handleChange: OnDropdownChange<number, DropdownOption<number>> = useCallback(
    (optionId, option) => {
      if (option == null || optionId == null) {
        return;
      }
      onChange?.(
        selectedSet.has(optionId)
          ? selected.filter(i => i !== optionId)
          : selected.concat([optionId])
      );
      if (selectedSet.has(optionId)) {
        onDeselectClient?.(optionId);
      } else {
        onSelectClient?.(optionId);
      }
    },
    [onChange, selectedSet, selected, onDeselectClient, onSelectClient]
  );

  const createHandleBulkAction = (action: 'all' | 'none') => () => {
    if (action === 'none') {
      onChange?.([]);
      onClear?.();
    }
    if (action === 'all') {
      onChange?.(filteredClients.map(client => client.id));
    }
  };

  const inputActions = (
    <>
      <button type="button" onClick={createHandleBulkAction('all')}>
        All
      </button>
      {allowSelectNone && (
        <button type="button" onClick={createHandleBulkAction('none')}>
          None
        </button>
      )}
    </>
  );

  const renderOption = useCallback(
    (item: DropdownOption<number>) => {
      const checked = selected.includes(item.value);
      return (
        <FilterDropDownItem item={item} checked={checked} disabled={disableAll} resource="client" />
      );
    },
    [selected, disableAll]
  );

  return (
    <div data-testid={props.testId}>
      <Popover
        width={352}
        isOpen={isOpen}
        onOpen={setOpen}
        onClose={setClosed}
        position={position}
        targetWrapper={false}
        target={
          <ClearableButton
            priority="medium"
            selected={isSelected}
            disabled={disabled}
            onClear={showClearButton ? handleClear : undefined}
            isActive={null}
            clearTooltip="Clear the client filter"
            className={styles.button}
            size={size}
          >
            <FontAwesome name="user" />
            <span>{label}</span>
            {isLoading && (
              <div
                className={classNames(styles.loadingIndicator, {
                  [styles.loadingIndicatorOpen]: isOpen,
                  [styles.loadingIndicatorDisabled]: disabled,
                })}
              >
                <div className={styles.spinnerContainer}>
                  <Spinner length={6} color="#000" />
                </div>
              </div>
            )}
          </ClearableButton>
        }
      >
        {position => (
          <DropdownBuilder
            value={null}
            values={selected}
            options={filteredClients}
            onChange={handleChange}
            onClear={handleClear}
            isOpen={isOpen}
            onSetIsOpen={setIsOpen}
            optionHeight={28}
            renderOption={renderOption}
            inputIcon={<FontAwesome name="search" />}
            inputCaret={false}
            placeholder="Search clients"
            inputActions={inputActions}
            disabled={disabled}
            filterString={filterString}
            onSetFilterString={onSetFilterString}
            highlightSelection={false}
            popoverFixed={fixed}
          >
            {context => (
              <div>
                <div style={{ padding: '12px 12px 0 12px' }}>
                  <FilterGroups
                    active={filterGroup}
                    groups={filterGroups}
                    onChange={setFilterGroup}
                  />
                </div>
                <div style={{ padding: '6px 12px 12px' }}>
                  {context.renderInput({ autoFocus: true, disabled })}
                </div>
                {context.renderList({ position })}
              </div>
            )}
          </DropdownBuilder>
        )}
      </Popover>
    </div>
  );
};

ClientFilterDropdown.defaultProps = {
  emptyLabel: 'Clients',
  unrestrictedWhenEmpty: true,
};

export default ClientFilterDropdown;
